import React, { useRef, useState } from "react";
import { uploadVideoInChunks } from "../../../../utils/chunkedUpload";

const emptyBlog = {
  title: "",
  summary: "",
  content: "",
  thumbnail: "",
  contentImages: [],
};

const htmlToBlogMarkdown = (html) => {
  const documentNode = new DOMParser().parseFromString(html, "text/html");

  const convert = (node, listType = null, listIndex = 0) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName.toLowerCase();
    if (["script", "style", "meta"].includes(tag)) return "";
    const children = Array.from(node.childNodes)
      .map((child, index) => convert(child, listType, index))
      .join("");
    const clean = children.trim();

    if (/^h[1-6]$/.test(tag)) {
      const level = Math.min(3, Number(tag.slice(1)) + 1);
      return `\n\n${"#".repeat(level)} ${clean}\n\n`;
    }
    if (tag === "p" || tag === "div") return clean ? `\n\n${clean}\n\n` : "";
    if (tag === "br") return "\n";
    if (tag === "strong" || tag === "b") return clean ? `**${clean}**` : "";
    if (tag === "em" || tag === "i") return clean ? `*${clean}*` : "";
    if (tag === "u") return clean ? `__${clean}__` : "";
    if (tag === "a") {
      const href = node.getAttribute("href") || "";
      return clean && /^https?:\/\//i.test(href) ? `[${clean}](${href})` : clean;
    }
    if (tag === "blockquote") {
      return `\n\n${clean.split("\n").map((line) => `> ${line}`).join("\n")}\n\n`;
    }
    if (tag === "ul" || tag === "ol") {
      return `\n\n${Array.from(node.children)
        .map((child, index) => convert(child, tag, index))
        .join("\n")}\n\n`;
    }
    if (tag === "li") return `${listType === "ol" ? `${listIndex + 1}.` : "-"} ${clean}`;
    if (tag === "pre") return clean ? `\n\n\`\`\`\n${node.textContent.trim()}\n\`\`\`\n\n` : "";
    if (tag === "code") return clean ? `\`${clean}\`` : "";
    return children;
  };

  return convert(documentNode.body)
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const BlogEditorForm = ({
  initialValue = emptyBlog,
  token,
  submitLabel,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    ...emptyBlog,
    ...initialValue,
  });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(
    initialValue.thumbnail || ""
  );
  const [contentImages, setContentImages] = useState(
    (initialValue.contentImages || []).map((image) => ({
      ...image,
      preview: image.url,
    }))
  );
  const [loading, setLoading] = useState(false);
  const contentRef = useRef(null);

  const changeField = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const selectCover = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const changeCoverUrl = (event) => {
    const thumbnail = event.target.value;
    setCoverFile(null);
    setCoverPreview(thumbnail);
    setFormData((current) => ({ ...current, thumbnail }));
  };

  const addUploadedImages = (event) => {
    const files = Array.from(event.target.files || []);
    setContentImages((current) => [
      ...current,
      ...files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        url: "",
        caption: "",
        alignment: "full",
        afterBlock: "",
      })),
    ]);
    event.target.value = "";
  };

  const addImageUrl = () => {
    setContentImages((current) => [
      ...current,
      {
        url: "",
        preview: "",
        caption: "",
        alignment: "full",
        afterBlock: "",
      },
    ]);
  };

  const updateImage = (index, field, value) => {
    setContentImages((current) =>
      current.map((image, imageIndex) => {
        if (imageIndex !== index) return image;
        const updated = { ...image, [field]: value };
        if (field === "url") updated.preview = value;
        return updated;
      })
    );
  };

  const formatSelection = (type) => {
    const textarea = contentRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = formData.content.slice(start, end);
    const fallback = type === "link" ? "link text" : "selected text";
    const text = selected || fallback;
    let replacement = text;

    if (type === "bold") replacement = `**${text}**`;
    if (type === "italic") replacement = `*${text}*`;
    if (type === "underline") replacement = `__${text}__`;
    if (type === "heading") {
      replacement = text
        .split("\n")
        .map((line) => `## ${line.replace(/^#+\s*/, "")}`)
        .join("\n");
    }
    if (type === "list") {
      replacement = text
        .split("\n")
        .map((line) => `- ${line.replace(/^[-*•]\s*/, "")}`)
        .join("\n");
    }
    if (type === "quote") {
      replacement = text
        .split("\n")
        .map((line) => `> ${line.replace(/^>\s*/, "")}`)
        .join("\n");
    }
    if (type === "link") {
      const url = window.prompt("Enter the link URL", "https://");
      if (!url) return;
      replacement = `[${text}](${url})`;
    }

    const content =
      formData.content.slice(0, start) +
      replacement +
      formData.content.slice(end);
    setFormData((current) => ({ ...current, content }));

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + replacement.length);
    });
  };

  const pasteFormattedContent = (event) => {
    const html = event.clipboardData?.getData("text/html");
    if (!html) return;

    const converted = htmlToBlogMarkdown(html);
    if (!converted) return;
    event.preventDefault();

    const textarea = contentRef.current;
    const start = textarea?.selectionStart ?? formData.content.length;
    const end = textarea?.selectionEnd ?? start;
    const before = formData.content.slice(0, start);
    const after = formData.content.slice(end);
    const prefix = before && !before.endsWith("\n") ? "\n\n" : "";
    const suffix = after && !after.startsWith("\n") ? "\n\n" : "";
    const content = `${before}${prefix}${converted}${suffix}${after}`;

    setFormData((current) => ({ ...current, content }));
    requestAnimationFrame(() => {
      const cursor = before.length + prefix.length + converted.length;
      textarea?.focus();
      textarea?.setSelectionRange(cursor, cursor);
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const thumbnail = coverFile
        ? await uploadVideoInChunks(coverFile, token)
        : formData.thumbnail.trim();

      if (!thumbnail) {
        throw new Error("Upload a cover image or enter a cover image URL.");
      }

      const images = await Promise.all(
        contentImages.map(async (image, index) => ({
          url: image.file
            ? await uploadVideoInChunks(image.file, token)
            : image.url.trim(),
          caption: image.caption?.trim() || "",
          alignment: image.alignment || "full",
          afterBlock:
            image.afterBlock === ""
              ? (index + 1) * 2
              : Math.max(0, Number(image.afterBlock) || 0),
        }))
      );

      const savedBlog = await onSubmit({
        title: formData.title.trim(),
        summary: formData.summary.trim(),
        content: formData.content.trim(),
        thumbnail,
        contentImages: images.filter((image) => image.url),
      });
      if (!savedBlog) {
        throw new Error(
          "The server did not save the blog. Check the error message above."
        );
      }
    } catch (error) {
      alert(error.message || "Unable to save the blog.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid max-w-5xl gap-6">
      <label className="grid gap-2">
        <span className="font-semibold">Blog title</span>
        <input
          name="title"
          value={formData.title}
          onChange={changeField}
          placeholder="Why Technical Analysis Is Your Gateway to Financial Freedom"
          className="rounded bg-richblack-700 px-4 py-3"
          required
        />
      </label>

      <label className="grid gap-2">
        <span className="font-semibold">Short summary</span>
        <textarea
          name="summary"
          rows={3}
          value={formData.summary}
          onChange={changeField}
          placeholder="A short introduction shown on the blog listing page."
          className="rounded bg-richblack-700 px-4 py-3"
          required
        />
      </label>

      <label className="grid gap-2">
        <span className="font-semibold">Full blog content</span>
        <div
          className="flex flex-wrap gap-2 rounded-t bg-richblack-800 p-2"
          aria-label="Content formatting toolbar"
        >
          <button
            type="button"
            onClick={() => formatSelection("bold")}
            className="rounded px-3 py-1.5 font-bold hover:bg-richblack-600"
            title="Bold selected text"
          >
            B
          </button>
          <button
            type="button"
            onClick={() => formatSelection("italic")}
            className="rounded px-3 py-1.5 italic hover:bg-richblack-600"
            title="Italicize selected text"
          >
            I
          </button>
          <button
            type="button"
            onClick={() => formatSelection("underline")}
            className="rounded px-3 py-1.5 underline hover:bg-richblack-600"
            title="Underline selected text"
          >
            U
          </button>
          <button
            type="button"
            onClick={() => formatSelection("heading")}
            className="rounded px-3 py-1.5 font-semibold hover:bg-richblack-600"
            title="Turn selected line into a heading"
          >
            Heading
          </button>
          <button
            type="button"
            onClick={() => formatSelection("list")}
            className="rounded px-3 py-1.5 hover:bg-richblack-600"
            title="Turn selected lines into a list"
          >
            • List
          </button>
          <button
            type="button"
            onClick={() => formatSelection("quote")}
            className="rounded px-3 py-1.5 hover:bg-richblack-600"
            title="Format selected text as a quote"
          >
            “ Quote
          </button>
          <button
            type="button"
            onClick={() => formatSelection("link")}
            className="rounded px-3 py-1.5 hover:bg-richblack-600"
            title="Add a link to selected text"
          >
            Link
          </button>
        </div>
        <textarea
          ref={contentRef}
          name="content"
          rows={28}
          value={formData.content}
          onChange={changeField}
          onPaste={pasteFormattedContent}
          placeholder={`Paste or write the complete article here.

Leave a blank line between paragraphs.
Use ## before a heading if you want to mark it manually.
Use - before list items.
Paste normal text freely; it will also be formatted automatically.`}
          className="min-h-[560px] resize-y rounded-b bg-richblack-700 px-4 py-3 font-sans leading-7"
          required
        />
        <span className="text-xs text-richblack-300">
          Paste a complete formatted article from ChatGPT. Headings, bold and
          italic text, links, quotes, bullet lists, numbered lists, and paragraph
          spacing are converted automatically.
        </span>
      </label>

      <section className="grid gap-3 rounded-lg border border-richblack-600 p-4">
        <h2 className="text-lg font-semibold">Cover image</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="grid gap-2">
            <span>Upload cover image</span>
            <input type="file" accept="image/*" onChange={selectCover} />
          </label>
          <label className="grid gap-2">
            <span>Or enter cover image URL</span>
            <input
              type="url"
              value={formData.thumbnail}
              onChange={changeCoverUrl}
              placeholder="https://example.com/cover-image.jpg"
              className="rounded bg-richblack-700 px-4 py-3"
            />
          </label>
        </div>
        {coverPreview && (
          <img
            src={coverPreview}
            alt="Cover preview"
            className="max-h-72 w-full rounded object-contain"
          />
        )}
      </section>

      <section className="grid gap-4 rounded-lg border border-richblack-600 p-4">
        <div>
          <h2 className="text-lg font-semibold">Images inside the blog</h2>
          <p className="text-sm text-richblack-300">
            Upload several images together or add image URLs. If placement is
            left empty, images are distributed automatically through the article.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="cursor-pointer rounded bg-richblack-700 px-4 py-2">
            Upload multiple images
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={addUploadedImages}
              className="hidden"
            />
          </label>
          <button
            type="button"
            onClick={addImageUrl}
            className="rounded border border-richblack-500 px-4 py-2"
          >
            + Add image URL
          </button>
        </div>

        {contentImages.map((image, index) => (
          <div
            key={`${image.preview || "image"}-${index}`}
            className="grid gap-3 rounded bg-richblack-700 p-3 md:grid-cols-[150px_1fr_auto]"
          >
            <div className="grid h-28 place-items-center overflow-hidden rounded bg-richblack-800 text-xs text-richblack-300">
              {image.preview ? (
                <img
                  src={image.preview}
                  alt=""
                  className="h-full w-full object-contain"
                />
              ) : (
                "Image preview"
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {!image.file && (
                <input
                  type="url"
                  value={image.url}
                  onChange={(event) =>
                    updateImage(index, "url", event.target.value)
                  }
                  placeholder="https://example.com/article-image.jpg"
                  className="rounded bg-richblack-800 px-3 py-2 sm:col-span-2"
                />
              )}
              <input
                value={image.caption || ""}
                onChange={(event) =>
                  updateImage(index, "caption", event.target.value)
                }
                placeholder="Caption (optional)"
                className="rounded bg-richblack-800 px-3 py-2"
              />
              <select
                value={image.alignment || "full"}
                onChange={(event) =>
                  updateImage(index, "alignment", event.target.value)
                }
                className="rounded bg-richblack-800 px-3 py-2"
              >
                <option value="full">Full width</option>
                <option value="center">Centered</option>
                <option value="left">Left aligned</option>
                <option value="right">Right aligned</option>
              </select>
              <input
                type="number"
                min="0"
                value={image.afterBlock ?? ""}
                onChange={(event) =>
                  updateImage(index, "afterBlock", event.target.value)
                }
                placeholder="Placement section (automatic)"
                className="rounded bg-richblack-800 px-3 py-2 sm:col-span-2"
              />
            </div>
            <button
              type="button"
              onClick={() =>
                setContentImages((current) =>
                  current.filter((_, imageIndex) => imageIndex !== index)
                )
              }
              className="self-start text-pink-200"
            >
              Remove
            </button>
          </div>
        ))}
      </section>

      <button
        type="submit"
        disabled={loading}
        className="rounded-md bg-yellow-100 px-6 py-3 font-semibold text-richblack-900 disabled:opacity-60"
      >
        {loading ? "Saving blog..." : submitLabel}
      </button>
    </form>
  );
};

export default BlogEditorForm;
